import { useCallback, useEffect, useState } from "react";
import { api, type Me } from "../lib/api";
import { useSession } from "../lib/auth-client";
import { linkProps, navigate } from "../lib/router";
import { Header, Notice, Spinner } from "../components/Layout";

interface AdminEvent {
  id: string;
  slug: string;
  name: string;
  sortOrder: number;
}

interface AdminGuest {
  id: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  side: string | null;
  contacted: string | null;
  status: string;
  partyMax: number;
  singapore: string | null;
  singaporeDinner: string | null;
  germany: string | null;
  vancouver: string | null;
  notes: string | null;
  invitedEventIds: string[];
  rsvps: Record<string, { attending: boolean; partySize: number }>;
}

interface Overview {
  events: AdminEvent[];
  presets: Record<string, string[]>;
  lastSync: { ok: boolean; at: string; error?: string; skipped?: string } | null;
  guests: AdminGuest[];
  stats: { total: number; pending: number; registered: number; awaiting: number };
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="card px-5 py-4">
      <p className="eyebrow">{label}</p>
      <p className={`mt-1 font-display text-3xl ${tone ?? ""}`}>{value}</p>
    </div>
  );
}

function ImportPanel({ onDone }: { onDone: () => void }) {
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ created: number; updated: number; problems: string[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setBusy(true);
    setError(null);
    try {
      setResult(await api.post("/api/admin/import", { csv }));
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <details className="card px-6 py-5">
      <summary className="cursor-pointer text-sm">Import guests from CSV</summary>
      <p className="mt-4 text-sm text-muted">
        Columns: <code className="text-ink">name</code>, <code className="text-ink">email</code>,{" "}
        <code className="text-ink">phone</code>, <code className="text-ink">party</code>,{" "}
        <code className="text-ink">notes</code>, and either{" "}
        <code className="text-ink">events</code> (slugs separated by <code>;</code>) or{" "}
        <code className="text-ink">preset</code>. Existing guests are matched by email and updated.
      </p>
      <textarea
        className="field mt-4 min-h-40 font-mono text-xs"
        value={csv}
        onChange={(e) => setCsv(e.target.value)}
        placeholder={"name,email,phone,party,events\nJordan Rivera,jordan@example.com,555-123-4567,2,ceremony;welcome-drinks"}
      />
      <div className="mt-3 flex items-center gap-3">
        <button className="btn btn-primary" onClick={run} disabled={busy || !csv.trim()}>
          {busy ? "Importing…" : "Import"}
        </button>
        {result && (
          <span className="text-sm text-sage">
            {result.created} created, {result.updated} updated
          </span>
        )}
      </div>
      {error && <div className="mt-3"><Notice tone="error">{error}</Notice></div>}
      {result?.problems.length ? (
        <ul className="mt-3 space-y-1 text-xs text-clay">
          {result.problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      ) : null}
    </details>
  );
}

function GuestRow({
  guest,
  events,
  onChange,
}: {
  guest: AdminGuest;
  events: AdminEvent[];
  onChange: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const invited = new Set(guest.invitedEventIds);

  async function toggle(eventId: string) {
    const next = new Set(invited);
    if (next.has(eventId)) next.delete(eventId);
    else next.add(eventId);
    setBusy(true);
    try {
      await api.patch(`/api/admin/guests/${guest.id}`, { eventIds: [...next] });
      onChange();
    } finally {
      setBusy(false);
    }
  }

  async function patch(body: Record<string, unknown>) {
    setBusy(true);
    try {
      await api.patch(`/api/admin/guests/${guest.id}`, body);
      onChange();
    } finally {
      setBusy(false);
    }
  }

  const statusTone =
    guest.status === "pending"
      ? "bg-[#fbeeea] text-clay"
      : guest.status === "registered"
        ? "bg-sage-soft text-sage"
        : "bg-[#f3ede6] text-muted";

  return (
    <tr className={`border-t border-line align-top ${busy ? "opacity-50" : ""}`}>
      <td className="px-4 py-3">
        <p className="text-sm">{guest.fullName}</p>
      </td>
      <td className="px-3 py-3 text-center text-sm">{guest.side ?? "—"}</td>
      <td className="px-3 py-3 text-center text-sm">{guest.contacted ?? "—"}</td>
      <td className="px-4 py-3 text-sm text-muted">{guest.email ?? "—"}</td>
      <td className="px-4 py-3 text-sm text-muted">{guest.phone ?? "—"}</td>
      <td className="px-4 py-3">
        <input
          type="number"
          min={0}
          defaultValue={Math.max(0, guest.partyMax - 1)}
          className="w-16 rounded border border-line bg-paper px-2 py-1 text-sm"
          onBlur={(e) => {
            const v = Math.max(0, Number(e.target.value) || 0);
            if (v !== guest.partyMax - 1) void patch({ partyMax: v + 1 });
          }}
        />
      </td>
      {[guest.singapore, guest.singaporeDinner, guest.germany, guest.vancouver].map(
        (invitation, index) => (
          <td key={index} className="px-3 py-3 text-center text-sm">
            {invitation ?? "—"}
          </td>
        ),
      )}
      <td className="max-w-48 px-4 py-3 text-sm text-muted">{guest.notes ?? "—"}</td>
      <td className="px-4 py-3">
        <span className={`rounded-full px-2 py-1 text-xs ${statusTone}`}>{guest.status}</span>
      </td>
      {events.map((ev) => {
        const rsvp = guest.rsvps[ev.id];
        return (
          <td key={ev.id} className="px-3 py-3 text-center">
            <label className="inline-flex cursor-pointer flex-col items-center gap-1">
              <input
                type="checkbox"
                checked={invited.has(ev.id)}
                onChange={() => void toggle(ev.id)}
                className="h-4 w-4 accent-[#6f7f63]"
              />
              {rsvp && (
                <span className={`text-[10px] ${rsvp.attending ? "text-sage" : "text-muted"}`}>
                  {rsvp.attending ? `yes${rsvp.partySize > 1 ? ` ×${rsvp.partySize}` : ""}` : "no"}
                </span>
              )}
            </label>
          </td>
        );
      })}
      <td className="px-4 py-3">
        <button
          className="text-xs text-muted underline decoration-line underline-offset-4 hover:text-clay"
          onClick={() => {
            if (confirm(`Remove ${guest.fullName} from the guest list?`)) {
              void api.del(`/api/admin/guests/${guest.id}`).then(onChange);
            }
          }}
        >
          remove
        </button>
      </td>
    </tr>
  );
}

export function Admin() {
  const { data: session, isPending: sessionLoading } = useSession();
  const [data, setData] = useState<Overview | null>(null);
  const [denied, setDenied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    try {
      setData(await api.get<Overview>("/api/admin/overview"));
      setDenied(false);
    } catch {
      setDenied(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (sessionLoading) return;
    if (!session) {
      navigate("/login", { replace: true });
      return;
    }
    void load();
  }, [session, sessionLoading, load]);

  if (sessionLoading || loading) {
    return (
      <div className="mx-auto max-w-6xl px-6">
        <Header />
        <Spinner label="Loading the guest list" />
      </div>
    );
  }

  if (denied) {
    return (
      <div className="mx-auto max-w-lg px-6">
        <Header />
        <div className="py-20 text-center">
          <h1 className="font-display text-3xl font-light">Not your table</h1>
          <p className="mt-4 text-muted">
            This page is for the couple. Add your email to <code>ADMIN_EMAILS</code> if it should be
            yours.
          </p>
          <a {...linkProps("/rsvp")} className="btn btn-ghost mt-8">
            Back to your invitation
          </a>
        </div>
      </div>
    );
  }

  const guests = data!.guests.filter((g) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return (
      g.fullName.toLowerCase().includes(q) ||
      (g.email ?? "").toLowerCase().includes(q) ||
      (g.phone ?? "").includes(q) ||
      (g.side ?? "").toLowerCase().includes(q) ||
      (g.notes ?? "").toLowerCase().includes(q) ||
      g.status.includes(q)
    );
  });

  return (
    <div className="mx-auto max-w-6xl px-6 pb-20">
      <Header
        right={
          <a {...linkProps("/rsvp")} className="btn btn-ghost text-sm">
            My invitation
          </a>
        }
      />

      <div className="flex flex-wrap items-end justify-between gap-4 pt-4 pb-8">
        <div>
          <p className="eyebrow">Admin</p>
          <h1 className="mt-2 font-display text-4xl font-light">Guest list</h1>
        </div>
        <div className="flex items-center gap-3">
          {data!.lastSync && (
            <span className="text-xs text-muted">
              {data!.lastSync.skipped
                ? "Sheet sync off"
                : data!.lastSync.ok
                  ? `Synced ${new Date(data!.lastSync.at).toLocaleTimeString()}`
                  : `Sync failed: ${data!.lastSync.error?.slice(0, 60)}`}
            </span>
          )}
          <button
            className="btn btn-ghost text-sm"
            disabled={syncing}
            onClick={async () => {
              setSyncing(true);
              try {
                await api.post("/api/admin/sync");
                await load();
              } finally {
                setSyncing(false);
              }
            }}
          >
            {syncing ? "Syncing…" : "Sync sheet now"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat label="On the list" value={data!.stats.total} />
        <Stat label="Signed up" value={data!.stats.registered} tone="text-sage" />
        <Stat label="Not yet signed in" value={data!.stats.awaiting} />
        <Stat label="Needs triage" value={data!.stats.pending} tone="text-clay" />
      </div>

      <div className="mt-6 space-y-4">
        <ImportPanel onDone={load} />
      </div>

      <input
        className="field mt-8 max-w-sm"
        placeholder="Search name, email, phone, status…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />

      <div className="card mt-4 overflow-x-auto">
        <table className="w-full min-w-[1400px] text-left">
          <thead>
            <tr className="bg-cream/60">
              <th className="px-4 py-3 text-xs font-normal tracking-widest text-muted uppercase">Name</th>
              <th className="px-3 py-3 text-center text-xs font-normal tracking-wide text-muted">E or N</th>
              <th className="px-3 py-3 text-center text-xs font-normal tracking-wide text-muted">Contacted</th>
              <th className="px-4 py-3 text-xs font-normal tracking-widest text-muted uppercase">Email</th>
              <th className="px-4 py-3 text-xs font-normal tracking-widest text-muted uppercase">Phone Number</th>
              <th className="px-4 py-3 text-xs font-normal tracking-wide text-muted">Plus X</th>
              <th className="px-3 py-3 text-center text-xs font-normal tracking-wide text-muted">Singapore</th>
              <th className="px-3 py-3 text-center text-xs font-normal tracking-wide text-muted">Singapore Dinner</th>
              <th className="px-3 py-3 text-center text-xs font-normal tracking-wide text-muted">Germany</th>
              <th className="px-3 py-3 text-center text-xs font-normal tracking-wide text-muted">Vancouver</th>
              <th className="px-4 py-3 text-xs font-normal tracking-widest text-muted uppercase">Notes</th>
              <th className="px-4 py-3 text-xs font-normal tracking-widest text-muted uppercase">Status</th>
              {data!.events.map((e) => (
                <th
                  key={e.id}
                  className="px-3 py-3 text-center text-xs font-normal tracking-wide text-muted"
                >
                  {e.name}
                </th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {guests.map((g) => (
              <GuestRow key={g.id} guest={g} events={data!.events} onChange={load} />
            ))}
            {guests.length === 0 && (
              <tr>
                <td colSpan={13 + data!.events.length} className="px-4 py-12 text-center text-sm text-muted">
                  No guests match that search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-xs text-muted">
        Checkboxes control which events each guest is invited to. Changes save immediately and push
        to the Google Sheet on the next sync.
      </p>
    </div>
  );
}
